import { useRestApiProvider } from "@jugl-web/rest-api";
import {
  KnowledgeItem,
  KnowledgeItemType,
} from "@jugl-web/rest-api/ai-workflows/types";
import { isAPIError, useToast, useTranslations } from "@jugl-web/utils";
import { useCallback, useMemo } from "react";
import { rateLimitEvents } from "@web-src/features/api/rateLimitEvents";

export const useAgentKnowledge = (entityId: string) => {
  const { toast } = useToast();
  const { t } = useTranslations();
  const { aiWorkflowsApi } = useRestApiProvider();

  const [deleteKnowledge] = aiWorkflowsApi.useDeleteKnowledgeMutation();

  const baseQuery = useMemo(
    () => ({
      agentId: entityId,
      entityId,
    }),
    [entityId]
  );

  const handleCXApiError = useCallback(
    (error: unknown) => {
      if (rateLimitEvents.isActive) return;
      if (isAPIError(error)) {
        if (error.status === 429) {
          return;
        }
        if (error.data.errors === "file_size_too_large") {
          toast(
            t({
              id: "feedback.file-size-is-too-large",
              defaultMessage: "File size is too large",
            }),
            { variant: "error" }
          );
          return;
        }

        if (error.data.errors === "limit exceeded") {
          toast(
            t({
              id: "cx-ai-agents-page.reached-max-knowledge-limit",
              defaultMessage: "Reached max knowledge limit",
            }),
            { variant: "error" }
          );
          return;
        }
      }

      toast(
        t({
          id: "feedback.something-went-wrong",
          defaultMessage: "Something went wrong",
        }),
        { variant: "error" }
      );
    },
    [t, toast]
  );

  const handleDeleteKnowledge = useCallback(
    async (id: string, type: KnowledgeItemType) => {
      try {
        await deleteKnowledge({ ...baseQuery, source_id: id, type });
      } catch (error) {
        handleCXApiError(error);
      }
    },
    [baseQuery, deleteKnowledge, handleCXApiError]
  );

  const {
    data: knowledgeInfo,
    isLoading: isKnowledgeInfoLoading,
    isError: isKnowledgeInfoError,
    refetch: knowledgeInfoRefetch,
  } = aiWorkflowsApi.useGetKnowledgeInfoQuery({
    ...baseQuery,
  });

  const {
    data: textKnowledgeRaw,
    isLoading: isTextKnowledgeLoading,
    isFetching: isTextKnowledgeFetching,
    isError: isTextKnowledgeError,
    refetch: textKnowledgeRefetch,
  } = aiWorkflowsApi.useGetKnowledgeQuery({
    ...baseQuery,
    type: "text",
  });

  const {
    data: quesKnowledgeRaw,
    isLoading: isQuesKnowledgeLoading,
    isFetching: isQuesKnowledgeFetching,
    isError: isQuesKnowledgeError,
    refetch: quesKnowledgeRefetch,
  } = aiWorkflowsApi.useGetKnowledgeQuery({
    ...baseQuery,
    type: "ques",
  });

  const {
    data: pdfKnowledgeRaw,
    isLoading: isPdfKnowledgeLoading,
    isFetching: isPdfKnowledgeFetching,
    isError: isPdfKnowledgeError,
    refetch: pdfKnowledgeRefetch,
  } = aiWorkflowsApi.useGetKnowledgeQuery({
    ...baseQuery,
    type: "pdf",
  });

  const {
    data: docxKnowledgeRaw,
    isLoading: isDocxKnowledgeLoading,
    isFetching: isDocxKnowledgeFetching,
    isError: isDocxKnowledgeError,
    refetch: docxKnowledgeRefetch,
  } = aiWorkflowsApi.useGetKnowledgeQuery({
    ...baseQuery,
    type: "docx",
  });

  const {
    data: linksKnowledgeRaw,
    isLoading: isLinksKnowledgeLoading,
    isFetching: isLinksKnowledgeFetching,
    isError: isLinksKnowledgeError,
    refetch: linksKnowledgeRefetch,
  } = aiWorkflowsApi.useGetKnowledgeQuery({
    ...baseQuery,
    type: "links",
  });

  const textKnowledge = textKnowledgeRaw as KnowledgeItem<"text">[] | undefined;
  const quesKnowledge = quesKnowledgeRaw as KnowledgeItem<"ques">[] | undefined;
  const pdfKnowledge = pdfKnowledgeRaw as KnowledgeItem<"pdf">[] | undefined;
  const docxKnowledge = docxKnowledgeRaw as KnowledgeItem<"docx">[] | undefined;
  const linksKnowledge = linksKnowledgeRaw as
    | KnowledgeItem<"links">[]
    | undefined;

  const allKnowledge = useMemo(
    () => [
      ...(textKnowledge || []),
      ...(quesKnowledge || []),
      ...(pdfKnowledge || []),
      ...(docxKnowledge || []),
      ...(linksKnowledge || []),
    ],
    [docxKnowledge, linksKnowledge, pdfKnowledge, quesKnowledge, textKnowledge]
  );

  const hasAnyKnowledge = useMemo(
    () => allKnowledge.length > 0,
    [allKnowledge]
  );

  const isAnyKnowledgeLoading =
    isTextKnowledgeLoading ||
    isQuesKnowledgeLoading ||
    isPdfKnowledgeLoading ||
    isDocxKnowledgeLoading ||
    isLinksKnowledgeLoading;

  const isAnyKnowledgeError =
    isTextKnowledgeError ||
    isQuesKnowledgeError ||
    isPdfKnowledgeError ||
    isDocxKnowledgeError ||
    isLinksKnowledgeError;

  const isAnyKnowledgeFetching =
    isTextKnowledgeFetching ||
    isQuesKnowledgeFetching ||
    isPdfKnowledgeFetching ||
    isDocxKnowledgeFetching ||
    isLinksKnowledgeFetching;

  const untrainedKnowledgeIds = useMemo(() => {
    if (isAnyKnowledgeLoading || isAnyKnowledgeFetching || isAnyKnowledgeError)
      return null;

    return allKnowledge
      .filter((item) =>
        item.data.type === "links"
          ? item.data.source.some(
              (sourceItem) => sourceItem.status === "not_trained"
            )
          : !item.data.metadata?.last_trained ||
            new Date(item.data.updated_at).getTime() >
              new Date(item.data.metadata.last_trained).getTime()
      )
      .map((item) => item.data.id);
  }, [
    allKnowledge,
    isAnyKnowledgeError,
    isAnyKnowledgeFetching,
    isAnyKnowledgeLoading,
  ]);

  const handleRetryKnowledge = () => {
    if (isTextKnowledgeError) textKnowledgeRefetch();
    if (isQuesKnowledgeError) quesKnowledgeRefetch();
    if (isPdfKnowledgeError) pdfKnowledgeRefetch();
    if (isDocxKnowledgeError) docxKnowledgeRefetch();
    if (isLinksKnowledgeError) linksKnowledgeRefetch();
  };

  const handleRefreshKnowledge = () => {
    textKnowledgeRefetch();
    quesKnowledgeRefetch();
    pdfKnowledgeRefetch();
    docxKnowledgeRefetch();
    linksKnowledgeRefetch();
  };

  return {
    isAnyKnowledgeLoading,
    isAnyKnowledgeFetching,
    isLinksKnowledgeFetching,
    isAnyKnowledgeError,
    textKnowledge,
    quesKnowledge,
    pdfKnowledge,
    docxKnowledge,
    linksKnowledge,
    knowledgeInfo,
    handleRetryKnowledge,
    handleRefreshKnowledge,
    handleDeleteKnowledge,
    handleCXApiError,
    hasAnyKnowledge,
    untrainedKnowledgeIds,
    isKnowledgeInfoLoading,
    isKnowledgeInfoError,
    knowledgeInfoRefetch,
  };
};
